// src/utils/layouts.jsx
import React from "react";
import { Box } from "@mui/material";

// 1. محتوى الصفحة (Page Content)
export const PageContainer = ({ children, maxWidth = "lg", ...props }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: (theme) => theme.breakpoints.values[maxWidth] || maxWidth,
      margin: "0 auto",
      padding: 2,
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 2. قائمة أفقية (Horizontal List)
export const HList = ({ children, scroll = false, gap = 1.5, ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: gap,
      flexWrap: scroll ? "nowrap" : "wrap",
      overflowX: scroll ? "auto" : "visible",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      paddingBottom: scroll ? 1 : 0,
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 3. قائمة عمودية (Vertical List)
export const VList = ({ children, scroll = false, gap = 1.5, ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: gap,
      overflowY: scroll ? "auto" : "visible",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 4. شبكة (Grid)
export const GridLayout = ({ 
  children, 
  scrollType = "none", 
  columns = "repeat(auto-fill, minmax(200px, 1fr))", 
  gap = 2, 
  ...props 
}) => {
  const scrollProps = {
    none: { overflow: "visible" },
    x: { overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" },
    y: { overflowY: "auto", overflowX: "hidden" },
    both: { overflow: "auto" },
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: scrollType === "x" ? "auto" : columns,
        gridAutoFlow: scrollType === "x" ? "column" : "row",
        gap: gap,
        padding: 1,
        ...scrollProps[scrollType],
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// 5. حاوية التوسيط المركزي (Centered Content)
export const CenterContent = ({ children, minHeight = "100%", ...props }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      minHeight: minHeight,
      textAlign: "center",
      width: "100%",
      ...props.sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// 6. معرض الكروت المتداخلة (المُعدّل لحل مشكلة الفراغات)
// تم تغيير النظام من columnCount إلى CSS Grid لحل مشكلة التوزيع
export const MasonryLayout = ({ 
  children, 
  columns = { xs: 1, sm: 2, md: 3, lg: 4 }, 
  gap = 2, 
  ...props 
}) => {
  // تحويل الكائن الممرر إلى قيم يقبلها الـ Grid للحفاظ على التجاوب
  const gridColumns = typeof columns === 'object' 
    ? Object.keys(columns).reduce((acc, key) => {
        acc[key] = `repeat(${columns[key]}, 1fr)`;
        return acc;
      }, {})
    : `repeat(${columns}, 1fr)`;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        gap: gap,
        width: "100%",
        alignItems: "start", // مهم جداً: يمنع الكروت من التمدد الطولي ويحافظ على اختلاف ارتفاعاتها
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

// 7. معرض الكروت متغيرة العرض (Horizontal Masonry / Justified Layout)
export const HorizontalMasonryLayout = ({ 
  children, 
  gap = 2, 
  ...props 
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: gap,
        width: "100%",
        ...props.sx,
        
        "& > *": {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
        },

        "&::after": {
          content: '""',
          flexGrow: 10,
        }
      }}
      {...props}
    >
      {children}
    </Box>
  );
};